import lsGet from './ls-get'
import deline from './deline'
import extractRawElm from './extract-raw-elm'

import { toBoolean } from './bits'
import { getElmStr } from './get-elm-str'
import { addSetToArray } from './arrayh'
import { flow, last, isString, castArray, kebabCase, camelCase, isBoolean, upperFirst, isFunction, escapeRegExp } from 'lodash-es'

const decode = (v) => isString(v) ? window.decodeURIComponent(v) : v
const exclamation = (v) => isString(v) ? v.replace(/\s*!?$/, '!') : v
const capitalCase = flow(camelCase, upperFirst)

export default  function getEnv({
  def,
  key = null,
  msg = null,
  elm = null,
  cast = toBoolean,
  check = isBoolean,
  print = console.info.bind(console),
  notPlh = ':not:',
  /* key override */
  urlSearchKey = null,
  urlPathRE = null,
  globalKey = null,
  lsKey = null,
  elmAttr = null,
  /* use source */
  useUrlSearch = true,
  useUrlPath = true,
  useGlobal = true,
  useLs = true,
  useElm = true,
  /* see location\elm */
  dataPrefix = 'data',
  safePrefix = 'ggs',
  isTrustedLocation = false
}) {
  if (isString(key)) key = castArray(key)
  if (isString(lsKey)) lsKey = castArray(lsKey)
  if (isString(elmAttr)) elmAttr = castArray(elmAttr)
  if (isString(globalKey)) globalKey = castArray(globalKey)
  if (isString(urlSearchKey)) urlSearchKey = castArray(urlSearchKey)

  const notRE = RegExp('\\s*' + escapeRegExp(notPlh) + '\\s*')
  const appendCamelSafePrefix = (v) => camelCase(safePrefix) + v
  const appendKebabSafePrefix = (v) => kebabCase(safePrefix) + '-' + v
  const removeKebabSafePrefix = (v) => v.replace(RegExp(`^${escapeRegExp(safePrefix)}-`), '')
  const removeKebabDataPrefix = (v) => v.replace(RegExp(`^${escapeRegExp(dataPrefix)}-`), '')
  const appendKebabDataPrefix = (v) => dataPrefix + '-' + v

  return define()

  function define() {
    const search = fromLocationSearch()
    if (check(search)) return search
    const path = fromLocationPath()
    if (check(path)) return path
    const global = fromGlobal()
    if (check(global)) return global
    const storage = fromLocalStorage()
    if (check(storage)) return storage
    const elm = fromElm()
    if (check(elm)) return elm
    return def
  }

  function smartPrint(val, toDel) {
    if (!msg) {
      return
    }

    const valMsg = isFunction(msg)
      ? msg(val)
      : msg.replace(notRE, val ? ' ' : ' not ')
    print(deline(`${exclamation(valMsg)} ${exclamation(toDel)}`))
  }

  function fromLocationSearch() {
    if (!useUrlSearch) {
      return null
    }

    const RE = (s) => RegExp('[&?]' + escapeRegExp(s).replace(/[-_]/g, '[-_]?') + '=(.*?)(?:\\?|&|$)', 'i')
    const get = (re) => cast(decode(last(re.exec(window.location.search))))
    const list = []

    if (key) {
      addSetToArray(list, key.map(kebabCase).map(appendKebabSafePrefix).map(RE))
      if (isTrustedLocation) {
        addSetToArray(list, key.map(kebabCase).map(RE))
      }
    }

    if (urlSearchKey) {
      addSetToArray(list, urlSearchKey.map(appendKebabSafePrefix).map(RE))
      if (isTrustedLocation) {
        addSetToArray(list, urlSearchKey.map(RE))
      }
    }

    for (const opt of list) {
      const val = get(opt)
      if (check(val)) {
        smartPrint(val, `To disable: delete "${opt}" part from ${window.location.href} url`)
        return val
      }
    }

    return null
  }

  function fromLocationPath() {
    if (!useUrlPath) {
      return null
    }

    if (!urlPathRE) {
      return null
    }

    if (!isTrustedLocation) {
      return null
    }

    const val = cast(decode(last(urlPathRE.exec(window.location.pathname))))
    if (check(val)) {
      smartPrint(val, `To disable: delete "${urlPathRE}" part from ${window.location.href} url`)
      return val
    } else {
      return null
    }
  }

  function fromGlobal() {
    if (!useGlobal) {
      return null
    }

    const list = []

    if (key) {
      addSetToArray(list, key.map(capitalCase).map(appendCamelSafePrefix))
      if (isTrustedLocation) {
        addSetToArray(list, key.map(camelCase))
      }
    }

    if (globalKey) {
      addSetToArray(list, globalKey.map(upperFirst).map(appendCamelSafePrefix))
      if (isTrustedLocation) {
        addSetToArray(list, globalKey)
      }
    }

    for (const opt of list) {
      const val = cast(window[opt])
      if (check(val)) {
        smartPrint(val, `To disable: delete window.${opt}`)
        return val
      }
    }

    return null
  }

  function fromLocalStorage() {
    if (!useLs) {
      return null
    }

    const list = []

    if (key) {
      addSetToArray(list, key.map(capitalCase).map(appendCamelSafePrefix))
      if (isTrustedLocation) {
        addSetToArray(list, key.map(camelCase))
      }
    }

    if (lsKey) {
      addSetToArray(list, lsKey.map(upperFirst).map(appendCamelSafePrefix))
      if (isTrustedLocation) {
        addSetToArray(list, key)
      }
    }

    for (const opt of list) {
      const val = cast(lsGet(opt))
      if (check(val)) {
        smartPrint(val, `To disable: delete window.localStorage.${opt}`)
        return val
      }
    }

    return null
  }

  function fromElm() {
    if (!elm) {
      return null
    }

    if (!useElm) {
      return null
    }

    const raw = extractRawElm(elm)
    const attrs = []

    if (key) {
      addSetToArray(attrs, key.map(kebabCase).map(appendKebabSafePrefix).map(appendKebabDataPrefix))
      addSetToArray(attrs, key.map(kebabCase).map(appendKebabSafePrefix))
      addSetToArray(attrs, key.map(kebabCase).map(appendKebabDataPrefix))
      addSetToArray(attrs, key.map(kebabCase))
    }

    if (elmAttr) {
      addSetToArray(attrs, elmAttr.map(removeKebabDataPrefix).map(appendKebabSafePrefix).map(appendKebabDataPrefix))
      addSetToArray(attrs, elmAttr.map(removeKebabDataPrefix).map(appendKebabSafePrefix))
      addSetToArray(attrs, elmAttr.map(removeKebabDataPrefix).map(appendKebabDataPrefix))
      addSetToArray(attrs, elmAttr.map(removeKebabDataPrefix))
    }

    for (const attr of attrs) {
      const val = cast(raw.getAttribute(attr))
      if (check(val)) {
        const canonicalAttr = appendKebabDataPrefix(removeKebabSafePrefix(removeKebabDataPrefix(attr)))
        if (canonicalAttr !== attr) {
          console.warn(`"${attr}" of ${getElmStr(raw)} elm should be replaced with "${canonicalAttr}" as "${attr}" is deprecated!`)
        }
        smartPrint(val, `To disable: delete "${attr}" attr of ${getElmStr(raw)} elm`)
        return val
      }
    }

    return null
  }
}
